import { Location } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  output,
  inject,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';
import type { AbstractControl, UntypedFormArray, UntypedFormGroup } from '@angular/forms';
import { ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { emailValidator } from '@core/common-legacy';
import { I18nService, TranslatePipe } from '@core/i18n-legacy';
import type { SelectOption } from '@core/user-interface-legacy';
import {
  ButtonComponent,
  FormFieldComponent,
  GroupComponent,
  IconButtonComponent,
  IconComponent,
  InputComponent,
  LabelComponent,
  SelectComponent,
  WindowRef,
} from '@core/user-interface-legacy';

@Component({
  selector: 'rt-invitation-form',
  templateUrl: 'invitation-form.component.html',
  styleUrls: ['./invitation-form.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ButtonComponent,
    FormFieldComponent,
    GroupComponent,
    IconButtonComponent,
    IconComponent,
    InputComponent,
    LabelComponent,
    SelectComponent,
    TranslatePipe,
    ReactiveFormsModule,
  ],
})
export class InvitationFormComponent {
  private readonly fb = inject(UntypedFormBuilder);
  private readonly i18n = inject(I18nService);
  private readonly windowRef = inject(WindowRef);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly pendingInvitesUrl: string;

  protected readonly cdRef = inject(ChangeDetectorRef);

  public readonly roles = input<SelectOption[]>(undefined!);
  public readonly currentAdminEmails = input<string[]>([]);
  public readonly invitedEmails = input<string[]>([]);

  public valueChanges = output<{
    valid: boolean;
    invitations: Array<{
      email: string;
      roleId: number;
    }>;
  }>();

  public invitationForm: UntypedFormArray;

  private ngOnDestroy$ = new Subject<void>();

  constructor() {
    this.pendingInvitesUrl = this.buildPendingInvitesUrl();
  }

  public ngOnInit(): void {
    this.invitationForm = this.fb.array([this.createFormGroup()]);
    this.invitationForm.valueChanges
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(value =>
        this.valueChanges.emit({ invitations: value, valid: this.invitationForm.valid }),
      );
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public trackByError(index: number, error: string): string {
    return error;
  }

  public getErrorMessages(control: AbstractControl): string[] {
    if (!control.errors || (control.errors && !Object.keys(control.errors).length)) {
      return [];
    }
    return Object.keys(control.errors)
      .map(key => {
        switch (key) {
          case 'userExists':
            return this.i18n.translate('app.invitation_form.user_exists');
          case 'alreadyInvited':
            return this.i18n.translate('app.invitation_form.already_invited_v2_html', {
              pendingInvitesUrl: this.pendingInvitesUrl,
            });
          default:
            return '';
        }
      })
      .filter(message => !!message);
  }

  public addInvitationForm(): void {
    this.invitationForm.push(this.createFormGroup());
  }

  public removeInvitationForm(index: number): void {
    this.invitationForm.removeAt(index);
  }

  public trackByControl(index: number, control: AbstractControl): AbstractControl {
    return control;
  }

  public userExistsValidator(control: AbstractControl): { userExists: true } | null {
    return control.value && this.currentAdminEmails().includes(control.value.toLowerCase())
      ? { userExists: true }
      : null;
  }

  public alreadyInvitedValidator(control: AbstractControl): { alreadyInvited: true } | null {
    return control.value && this.invitedEmails().includes(control.value.toLowerCase())
      ? { alreadyInvited: true }
      : null;
  }

  private createFormGroup(): UntypedFormGroup {
    return this.fb.group({
      email: [
        '',
        [
          Validators.required,
          emailValidator,
          this.userExistsValidator.bind(this),
          this.alreadyInvitedValidator.bind(this),
        ],
      ],
      roleId: ['', [Validators.required]],
    });
  }

  private buildPendingInvitesUrl(): string {
    const urlTree = this.router.createUrlTree(['/settings/members/invites'], {
      queryParams: { page: 1, limit: 15 },
      queryParamsHandling: 'merge',
    });
    return (
      this.windowRef.nativeWindow.location.origin +
      this.location.prepareExternalUrl(this.router.serializeUrl(urlTree))
    );
  }
}
